import { matchPath } from "react-router";

import {
  BETA_LOCK_ENABLED,
  NUTRITION_PROGRAM_ENABLED,
  PANEL_LEGACY_URL,
} from "@config/app";
import type { SidebarGroupType } from "@UI/types/sidebar.ts";

import {
  ACCOUNT_SETTINGS,
  CLIENT,
  CLIENT_PROGRAMS_NEW,
  CLINIC_SETTINGS,
  CLINIC_SETTINGS_PAYMENTS,
  CLINIC_SETTINGS_PLAN,
  DASHBOARD,
  DIETICIAN_SETTINGS,
  DIETICIAN_SETTINGS_ONLINE_PAYMENTS,
  ONBOARDING,
  PATIENT_AI_NOTES,
  PLANS_AND_PAYMENTS,
  PRICING,
  PROGRAMS,
  SETTINGS,
} from "./routes";

const ALLOWED_PATTERNS = [
  DASHBOARD,
  CLINIC_SETTINGS_PAYMENTS,
  CLINIC_SETTINGS_PLAN,
  `${CLINIC_SETTINGS_PLAN}/*`,
  DIETICIAN_SETTINGS_ONLINE_PAYMENTS,
  ONBOARDING,
  PATIENT_AI_NOTES,
  PLANS_AND_PAYMENTS,
  `${PLANS_AND_PAYMENTS}/*`,
  PRICING,
  `${PRICING}/*`,
];

export const isBetaLockAllowed = (pathname: string): boolean =>
  ALLOWED_PATTERNS.some(pattern => matchPath(pattern, pathname) !== null);

// The nutrition program (PŻ) is not released yet: release builds set
// VITE_APP_NUTRITION_PROGRAM=false and hand these pages to the legacy panel.
const NUTRITION_PROGRAM_PATTERNS = [
  PROGRAMS,
  `${PROGRAMS}/*`,
  CLIENT_PROGRAMS_NEW,
  `${CLIENT_PROGRAMS_NEW}/*`,
  `${CLIENT}/nutritional-programs/*`,
];

export const isNutritionProgramPath = (pathname: string): boolean =>
  NUTRITION_PROGRAM_PATTERNS.some(
    pattern => matchPath(pattern, pathname) !== null,
  );

export const isRouteAvailable = (pathname: string): boolean =>
  (!BETA_LOCK_ENABLED || isBetaLockAllowed(pathname)) &&
  (NUTRITION_PROGRAM_ENABLED || !isNutritionProgramPath(pathname));

const LEGACY_EQUIVALENTS = [
  [ACCOUNT_SETTINGS, SETTINGS],
  [CLINIC_SETTINGS, DIETICIAN_SETTINGS],
] as const;

const isWithin = (path: string, route: string) =>
  path === route ||
  ["/", "?", "#"].some(next => path.startsWith(`${route}${next}`));

const toLegacyPath = (path: string): string =>
  LEGACY_EQUIVALENTS.find(([current]) => isWithin(path, current))?.[1] ?? path;

export const legacyHandOverUrl = (path: string): string | null =>
  PANEL_LEGACY_URL ? `${PANEL_LEGACY_URL}${toLegacyPath(path)}` : null;

export const legacyPanelUrl = (path: string): string | null =>
  BETA_LOCK_ENABLED ? legacyHandOverUrl(path) : null;

export const filterBetaLockSidebar = (
  groups: SidebarGroupType[],
): SidebarGroupType[] => {
  if (!BETA_LOCK_ENABLED && NUTRITION_PROGRAM_ENABLED) return groups;

  return groups
    .map(group => ({
      ...group,
      children: group.children.filter(item =>
        item.soon
          ? !BETA_LOCK_ENABLED
          : !item.href || isRouteAvailable(item.href),
      ),
    }))
    .filter(group => group.children.length > 0);
};
